import { useState, useMemo, useEffect } from 'react';
import { formatDateToISO } from '../utils';
import { useAttendanceCalendar } from './api';

export interface WeekDay {
  date: Date;
  isToday: boolean;
  isSelected: boolean;
  status?: 'present' | 'absent' | 'late' | 'early_leave';
}

/**
 * カレンダー週の表示とナビゲーションを管理するカスタムフック
 *
 * @param selectedDate - 現在選択されている日付（undefinedの場合はローディング中）
 * @param patientId - 患者ID
 * @returns 週の日付データとナビゲーション関数
 */
export const useCalendarWeek = (selectedDate: Date | undefined, patientId: string | number | null) => {
  const patientIdNumber = patientId ? Number(patientId) : null;
  const [calendarMonth, setCalendarMonth] = useState<Date | undefined>(
    selectedDate ? new Date(selectedDate) : undefined
  );

  // 日付更新時にカレンダーを更新
  useEffect(() => {
    if (selectedDate) {
      setCalendarMonth(new Date(selectedDate));
    }
  }, [selectedDate]);

  // カレンダーの週の日付範囲を計算
  const weekDateRange = useMemo(() => {
    // calendarMonthがundefinedの場合はnullを返す
    if (!calendarMonth) {
      return null;
    }

    const baseDate = new Date(calendarMonth);
    const dayOfWeek = baseDate.getDay();

    const weekStart = new Date(baseDate);
    weekStart.setDate(baseDate.getDate() - dayOfWeek);
    weekStart.setHours(0, 0, 0, 0);

    const weekEnd = new Date(weekStart);
    weekEnd.setDate(weekStart.getDate() + 6);
    weekEnd.setHours(23, 59, 59, 999);

    return {
      startDate: formatDateToISO(weekStart),
      endDate: formatDateToISO(weekEnd),
      weekStart,
    };
  }, [calendarMonth]);

  // 出席カレンダーデータを取得（weekDateRangeがnullの場合はスキップ）
  const { data: attendanceData } = useAttendanceCalendar(
    weekDateRange ? patientIdNumber : null,
    weekDateRange?.startDate ?? '',
    weekDateRange?.endDate ?? ''
  );

  // 出席データを日付でマッピング
  const attendanceByDate = useMemo(() => {
    if (!attendanceData?.contact_logs) return {};

    const map: Record<string, 'present' | 'absent' | 'late' | 'early_leave'> = {};
    attendanceData.contact_logs.forEach(log => {
      map[log.date] = log.attendance_status;
    });
    return map;
  }, [attendanceData]);

  // 1週間分の日付生成
  const weekDays = useMemo((): WeekDay[] => {
    // selectedDateまたはweekDateRangeがない場合は空配列を返す
    if (!selectedDate || !weekDateRange) {
      return [];
    }

    const days: WeekDay[] = [];

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const selected = new Date(selectedDate);
    selected.setHours(0, 0, 0, 0);

    for (let i = 0; i < 7; i++) {
      const currentDate = new Date(weekDateRange.weekStart);
      currentDate.setDate(weekDateRange.weekStart.getDate() + i);
      currentDate.setHours(0, 0, 0, 0);

      const isToday = currentDate.getTime() === today.getTime();
      const isSelected = currentDate.getTime() === selected.getTime();

      const dateString = formatDateToISO(currentDate);
      const status = attendanceByDate[dateString];

      days.push({
        date: currentDate,
        isToday,
        isSelected,
        status,
      });
    }

    return days;
  }, [weekDateRange, selectedDate, attendanceByDate]);

  /**
   * 前の週に移動
   */
  const goToPrevWeek = () => {
    setCalendarMonth(prev => {
      if (!prev) return prev;
      const newDate = new Date(prev);
      newDate.setDate(newDate.getDate() - 7);
      return newDate;
    });
  };

  /**
   * 次の週に移動
   */
  const goToNextWeek = () => {
    setCalendarMonth(prev => {
      if (!prev) return prev;
      const newDate = new Date(prev);
      newDate.setDate(newDate.getDate() + 7);
      return newDate;
    });
  };

  return {
    weekDays,
    calendarMonth,
    setCalendarMonth,
    goToPrevWeek,
    goToNextWeek,
  };
};
